import { useEffect, useState } from 'react'
import type { ELK, ElkNode } from 'elkjs/lib/elk-api'
import type { ModelElement, Relationship } from '@/frontend/features/Fde/types'

const NODE_WIDTH = 220
const NODE_HEIGHT = 88
let elkPromise: Promise<ELK> | null = null

function loadElk() {
  elkPromise ||= import('elkjs/lib/elk.bundled.js').then(({ default: ELK }) => new ELK())
  return elkPromise
}

export interface GraphPoint {
  x: number
  y: number
}

export interface PositionedElement {
  element: ModelElement
  position: GraphPoint
}

export interface RoutedRelationship {
  relationship: Relationship
  points: GraphPoint[]
}

export interface OntologyGraphLayoutState {
  nodes: PositionedElement[]
  edges: RoutedRelationship[]
  loading: boolean
  revision: number
  orientation: 'horizontal' | 'vertical'
}

export function useOntologyGraphLayout(elements: ModelElement[], relationships: Relationship[], anchorIds: string[] = []) {
  const orientation = anchorIds.length > 0 ? 'vertical' : 'horizontal'
  const [state, setState] = useState<OntologyGraphLayoutState>({ nodes: [], edges: [], loading: true, revision: 0, orientation })

  useEffect(() => {
    let cancelled = false
    setState((current) => ({ ...current, loading: true, orientation }))
    const anchors = new Set(anchorIds)
    const orderedElements = [
      ...anchorIds.flatMap((id) => elements.find((element) => String(element.id) === id) || []),
      ...elements.filter((element) => !anchors.has(String(element.id))),
    ]
    const reversedRelationshipIds = new Set<string>()
    const layoutRelationships = relationships.flatMap((relationship) => {
      const id = String(relationship.id)
      const fromId = String(relationship.from.id)
      const toId = String(relationship.to.id)
      const fromIsAnchor = anchors.has(fromId)
      const toIsAnchor = anchors.has(toId)

      // FIRST_SEPARATE requires focus nodes without incoming layout edges.
      // Reverse only the layout route; rendered points are reversed back below.
      if (fromIsAnchor && toIsAnchor) return []
      if (toIsAnchor) {
        reversedRelationshipIds.add(id)
        return [{ id, sources: [toId], targets: [fromId] }]
      }
      return [{ id, sources: [fromId], targets: [toId] }]
    })

    const graph: ElkNode = {
      id: 'ontology',
      layoutOptions: {
        'elk.algorithm': 'layered',
        'elk.direction': orientation === 'vertical' ? 'DOWN' : 'RIGHT',
        'elk.edgeRouting': 'ORTHOGONAL',
        'elk.spacing.nodeNode': orientation === 'vertical' ? '76' : '56',
        'elk.spacing.componentComponent': '90',
        'elk.layered.spacing.nodeNodeBetweenLayers': orientation === 'vertical' ? '92' : '120',
        'elk.layered.spacing.edgeNodeBetweenLayers': '36',
        'elk.layered.spacing.edgeEdgeBetweenLayers': '22',
        'elk.layered.layering.strategy': anchors.size > 0 ? 'COFFMAN_GRAHAM' : 'NETWORK_SIMPLEX',
        'elk.layered.layering.coffmanGraham.layerBound': anchors.size > 0 ? '4' : '2147483647',
        'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
        'elk.layered.crossingMinimization.greedySwitch.type': 'TWO_SIDED',
        'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
        'elk.layered.nodePlacement.favorStraightEdges': 'true',
        'elk.layered.cycleBreaking.strategy': anchors.size > 0 ? 'MODEL_ORDER' : 'GREEDY',
        'elk.layered.thoroughness': '30',
      },
      children: orderedElements.map((element) => ({
        id: String(element.id),
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        // FIRST_SEPARATE reserves an exclusive lane for the selected focus;
        // regular nodes cannot be mixed into it even when they reduce crossings.
        layoutOptions: anchors.has(String(element.id)) ? { 'org.eclipse.elk.layered.layering.layerConstraint': 'FIRST_SEPARATE' } : undefined,
      })),
      edges: layoutRelationships,
    }

    loadElk().then((elk) => elk.layout(graph)).then((result) => {
      if (cancelled) return
      const nodesById = new Map(elements.map((element) => [String(element.id), element]))
      const nodes = (result.children || []).flatMap((node) => {
        const element = nodesById.get(node.id)
        return element ? [{ element, position: { x: node.x || 0, y: node.y || 0 } }] : []
      })
      const routedPoints = new Map((result.edges || []).flatMap((edge) => {
        const section = edge.sections?.[0]
        if (!section) return []
        const points = [section.startPoint, ...(section.bendPoints || []), section.endPoint]
        return [[edge.id, reversedRelationshipIds.has(edge.id) ? points.reverse() : points] as const]
      }))
      const positionsById = new Map(nodes.map(({ element, position }) => [String(element.id), position]))
      const focusRelationships = relationships.filter((relationship) => (
        anchors.has(String(relationship.from.id)) && anchors.has(String(relationship.to.id))
      ))
      const edges = relationships.map((relationship) => {
        const id = String(relationship.id)
        const routed = routedPoints.get(id)
        if (routed) return { relationship, points: routed }

        const focusIndex = focusRelationships.findIndex((candidate) => String(candidate.id) === id)
        const from = positionsById.get(String(relationship.from.id))
        const to = positionsById.get(String(relationship.to.id))
        if (focusIndex < 0 || !from || !to) return { relationship, points: [] }

        const laneY = Math.min(from.y, to.y) - 30 - focusIndex * 16
        return {
          relationship,
          points: [
            { x: from.x + NODE_WIDTH / 2, y: from.y },
            { x: from.x + NODE_WIDTH / 2, y: laneY },
            { x: to.x + NODE_WIDTH / 2, y: laneY },
            { x: to.x + NODE_WIDTH / 2, y: to.y },
          ],
        }
      })
      setState((current) => ({ nodes, edges, loading: false, revision: current.revision + 1, orientation }))
    }).catch(() => {
      if (cancelled) return
      setState((current) => ({
        nodes: elements.map((element, index) => ({
          element,
          position: { x: (index % 4) * (NODE_WIDTH + 80), y: Math.floor(index / 4) * (NODE_HEIGHT + 60) },
        })),
        edges: relationships.map((relationship) => ({ relationship, points: [] })),
        loading: false,
        revision: current.revision + 1,
        orientation,
      }))
    })

    return () => { cancelled = true }
  }, [anchorIds, elements, orientation, relationships])

  return state
}

export const ontologyNodeSize = { width: NODE_WIDTH, height: NODE_HEIGHT }
